import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import AssetHeader from './components/AssetHeader';
import BarcodeDisplay from './components/BarcodeDisplay';
import GeneralInformation from './components/GeneralInformation';
import TechnicalSpecifications from './components/TechnicalSpecifications';
import FinancialDetails from './components/FinancialDetails';
import LocationHistory from './components/LocationHistory';
import MaintenanceRecords from './components/MaintenanceRecords';
import OwnerManagement from './components/OwnerManagement';
import StatusUpdateControls from './components/StatusUpdateControls';

const AssetDetailManagement = () => {
  const [searchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Mock asset data - in real app, this would come from API
  const mockAsset = {
    id: searchParams?.get('id') || 'AT-2025-0156',
    assetId: 'AT-2025-0156',
    name: 'Dell Latitude 7420 Laptop',
    serialNumber: 'DL7420-2025-001',
    assetType: 'laptop',
    brand: 'Dell',
    model: 'Latitude 7420',
    status: 'active',
    condition: 'good',
    purchaseDate: '2025-01-15',
    warrantyExpiration: '2028-01-15',
    description: 'High-performance business laptop for IT department',
    isCritical: true,
    requiresSpecialHandling: false,
    includeInAudit: true,
    notes: 'Primary work laptop assigned to IT administrator. Includes extended warranty and premium support.',
    lastUpdated: '2025-08-15T10:30:00Z',
    
    technicalSpecs: {
      processor: 'Intel Core i7-1185G7',
      ram: '16gb',
      storage: '512gb',
      graphics: 'Intel Iris Xe Graphics',
      displaySize: '14 inches',
      resolution: '1920x1080',
      operatingSystem: 'windows11',
      osVersion: '22H2',
      networkConnectivity: 'Wi-Fi 6, Bluetooth 5.1, Ethernet',
      ports: '2x USB-A, 2x USB-C, HDMI, Audio Jack',
      powerRequirements: '65W USB-C adapter',
      dimensions: '32.1 x 21.2 x 1.8 cm',
      antivirus: 'Windows Defender',
      officeSuite: 'Microsoft Office 365',
      developmentTools: 'Visual Studio Code, Git',
      otherSoftware: 'Chrome, Firefox, Slack, Teams',
      technicalNotes: 'Configured with corporate security policies and domain join.'
    },

    financialDetails: {
      purchasePrice: 1299.99,
      currency: 'inr',
      purchaseOrderNumber: 'PO-2025-IT-001',
      vendor: 'Dell Technologies',
      invoiceNumber: 'INV-DELL-2025-001',
      paymentMethod: 'Corporate Credit Card',
      depreciationMethod: 'straight-line',
      usefulLifeYears: 4,
      salvageValue: 200.00,
      insuranceValue: 1100.00,
      costCenter: 'CC-IT-001',
      budgetCode: 'BUD-2025-IT',
      glAccount: '1200-IT-EQUIP',
      financialNotes: 'Purchased under corporate volume licensing agreement with 3-year warranty extension.'
    }
  };

  const tabs = [
    { id: 'general', label: 'General Information', icon: 'Info' },
    { id: 'technical', label: 'Technical Specs', icon: 'Cpu' },
    { id: 'financial', label: 'Financial Details', icon: 'DollarSign' },
    { id: 'location', label: 'Location History', icon: 'MapPin' },
    { id: 'maintenance', label: 'Maintenance', icon: 'Wrench' },
    { id: 'owner', label: 'Owner Management', icon: 'User' },
    { id: 'status', label: 'Status Updates', icon: 'Activity' }
  ];

  useEffect(() => {
    // Simulate loading asset data
    const loadAsset = async () => {
      setLoading(true);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setAsset(mockAsset);
      setLoading(false);
    };

    loadAsset();
  }, [searchParams]);

  const handleAssetUpdate = (updatedAsset) => {
    setAsset(updatedAsset);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    // Simulate save operation
    await new Promise(resolve => setTimeout(resolve, 1500));
    setHasUnsavedChanges(false);
    setIsEditing(false);
    setLoading(false);
    console.log('Asset saved:', asset);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmCancel) return;
    }
    setIsEditing(false);
    setHasUnsavedChanges(false);
    // Reset asset to original state
    setAsset(mockAsset);
  };

  const handleDelete = () => {
    const confirmDelete = window.confirm('Are you sure you want to delete this asset? This action cannot be undone.');
    if (confirmDelete) {
      console.log('Asset deleted:', asset?.assetId);
      // In real app, navigate back to asset list
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        
        <main className={`transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'} pt-16`}>
          <div className="p-6">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading asset details...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        
        <main className={`transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'} pt-16`}>
          <div className="p-6">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚠️</span>
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Asset Not Found</h2>
                <p className="text-muted-foreground">The requested asset could not be found.</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <GeneralInformation 
            asset={asset} 
            isEditing={isEditing} 
            onUpdate={handleAssetUpdate} 
          />
        );
      case 'technical':
        return (
          <TechnicalSpecifications 
            asset={asset} 
            isEditing={isEditing} 
            onUpdate={handleAssetUpdate} 
          />
        );
      case 'financial':
        return (
          <FinancialDetails 
            asset={asset} 
            isEditing={isEditing} 
            onUpdate={handleAssetUpdate}
            userRole="admin" // In real app, get from auth context
          />
        );
      case 'location':
        return (
          <LocationHistory 
            asset={asset} 
            isEditing={isEditing} 
            onUpdate={handleAssetUpdate} 
          />
        );
      case 'maintenance':
        return (
          <MaintenanceRecords 
            asset={asset} 
            isEditing={isEditing} 
            onUpdate={handleAssetUpdate} 
          />
        );
      case 'owner':
        return (
          <OwnerManagement 
            asset={asset} 
            isEditing={isEditing} 
            onUpdate={handleAssetUpdate} 
          />
        );
      case 'status':
        return (
          <StatusUpdateControls 
            asset={asset} 
            isEditing={isEditing} 
            onUpdate={handleAssetUpdate} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'} pt-16`}>
        <div className="p-6">
          <Breadcrumb />
          
          {/* Asset Header */}
          <AssetHeader
            asset={asset}
            onEdit={handleEdit}
            onPrint={handlePrint}
            onDelete={handleDelete}
            isEditing={isEditing}
            onSave={handleSave}
            onCancel={handleCancel}
          />

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-3">
              {/* Tab Navigation */}
              <div className="bg-card border border-border rounded-lg mb-6 shadow-subtle">
                <div className="border-b border-border">
                  <nav className="flex space-x-8 px-6" aria-label="Tabs">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                          activeTab === tab?.id
                            ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <span>{tab?.label}</span>
                        </div>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Tab Content */}
              {renderTabContent()}
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1 space-y-6">
              {/* Barcode Display */}
              <BarcodeDisplay 
                assetId={asset?.assetId} 
                serialNumber={asset?.serialNumber} 
              />

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-4 shadow-subtle">
                <h3 className="text-md font-semibold text-foreground mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-smooth flex items-center space-x-2">
                    <span>📋</span>
                    <span>Generate Report</span>
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-smooth flex items-center space-x-2">
                    <span>📤</span>
                    <span>Export Data</span>
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-smooth flex items-center space-x-2">
                    <span>🔄</span>
                    <span>Sync with ERP</span>
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-smooth flex items-center space-x-2">
                    <span>📧</span>
                    <span>Send Notification</span>
                  </button>
                </div>
              </div>

              {/* Asset Summary */}
              <div className="bg-card border border-border rounded-lg p-4 shadow-subtle">
                <h3 className="text-md font-semibold text-foreground mb-4">Asset Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Asset Age</span>
                    <span className="text-foreground font-medium">
                      {Math.floor((new Date() - new Date(asset.purchaseDate)) / (1000 * 60 * 60 * 24 * 365))} years
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Warranty Status</span>
                    <span className={`font-medium ${
                      new Date(asset.warrantyExpiration) > new Date() 
                        ? 'text-success' :'text-error'
                    }`}>
                      {new Date(asset.warrantyExpiration) > new Date() ? 'Active' : 'Expired'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Maintenance</span>
                    <span className="text-foreground font-medium">Aug 15, 2025</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Next Audit</span>
                    <span className="text-foreground font-medium">Sep 30, 2025</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AssetDetailManagement;